import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTopButton from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHelmet from '@/components/base/SEOHelmet';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles
    .filter((a) => a.category === article?.category && a.slug !== slug)
    .slice(0, 4);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:underline mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: article.categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleSchema = {
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.publishDate,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '国产传媒',
    },
  };

  return (
    <>
      <SchemaOrg type="Article" data={articleSchema} />
      <SEOHelmet
        title={article.title}
        description={article.excerpt}
        keywords={`${article.tags.join(',')},国产传媒`}
        canonicalUrl={`https://www.guochuanmedia.com/article/${slug}`}
        ogType="article"
      />

      <Navbar />
      <article className="pt-20">
        <div className="max-w-[900px] mx-auto px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="mb-8">
            <span className="inline-block px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded-full mb-4 whitespace-nowrap">
              {article.categoryName}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.publishDate}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </div>

          <div className="aspect-[16/9] w-full overflow-hidden rounded-lg mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 国产传媒`}
              className="w-full h-full object-cover"
            />
          </div>

          <div
            className="prose prose-lg max-w-none mb-12 text-foreground-800 leading-relaxed article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 mb-12">
            {article.tags.map((tag) => (
              <span key={tag} className="px-3 py-1 text-xs bg-background-100 text-foreground-600 rounded-full whitespace-nowrap">
                #{tag}
              </span>
            ))}
          </div>

          <div className="border-t border-background-200/60 pt-10 mb-16">
            <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">相关推荐</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.slug} article={ra} />
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <ScrollToTopButton />
    </>
  );
}